import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { ResolutionKeys, StateToShow } from "../types";
import { MapContainer, TileLayer, GeoJSON, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Legend from "./Legend";
import OverlayLegend from "./OverlayLegend";
import chroma from "chroma-js";

import stateData from "./map_boundaries/stateData"
import countyData from "./map_boundaries/countyData"
import huc2Data from "./map_boundaries/huc2Data"
import huc4Data from "./map_boundaries/huc4Data"
import huc6Data from "./map_boundaries/huc6Data"
import huc8Data from "./map_boundaries/huc8Data"

import useSessionStorage from "./Session";
import ResetMapButton from "./ResetMap";
import L from "leaflet";

interface MapImageProps {
  "resolution": ResolutionKeys;
  "polygon_values": { [key: string]: string; };
  "min": number | undefined;
  "max": number | undefined;
  "categories": { [key: string]: number };
  "polygon_type": "Continuous" | "Discrete";
  "attribute_name": string;
  "current_dataset": string;
  "colormap_types": string;
  "colormap_bases": string;
  "attributes_list": { names: string[]; titles: string[] };
  "current_parameters": StateToShow;
  "available_datasets": { names: string[]; titles: string[] };
  "selectedOverlay": string;
  "attribute_legend_labels": string;
  "is_hml": boolean;
  "is_legend": string;
  "show_coloring" : boolean,
  "is_log" : boolean,
  "default_zoom_level"?: number,
  "max_zoom"?: number,
  "min_zoom"?: number,
  "is_overlay": string;
  "geojson_frame": GeoJSON.FeatureCollection | undefined;
  "popup_name" : {[key: string]: string};
  "popup_description" :  { [key: string]: string[]} | undefined;

  "zoomLevel" : number;
  "setZoomLevel" : (value: number | ((prev: number) => number)) => void;
  "center" : [number, number];
  "setCenter" : (value: [number, number] | ((prev: [number, number]) => [number, number] )) => void;
  
  "onMapChange"?: (zoom: number, center: [number, number]) => void;
  "isUpdating"?: boolean; // Flag to prevent recursive updates
}

// Create a component to handle map synchronization
const MapSyncHandler: React.FC<{
  zoomLevel: number;
  center: [number, number];
  setZoomLevel: (value: number | ((prev: number) => number)) => void;
  setCenter: (value: [number, number] | ((prev: [number, number]) => [number, number])) => void;
  onMapChange?: (zoom: number, center: [number, number]) => void;
  isUpdating?: boolean;
}> = ({ zoomLevel, center, setZoomLevel, setCenter, onMapChange, isUpdating }) => {
  const map = useMap();
  
  // Update map when props change (from other maps)
  useEffect(() => {
    if (isUpdating && map) {
      const currentZoom = map.getZoom();
      const currentCenter = map.getCenter();
      
      // Only update if values are different
      if (currentZoom !== zoomLevel || currentCenter.lat !== center[0] || currentCenter.lng !== center[1]) {
        map.setView(center, zoomLevel, { animate: false });
      }
    }
  }, [zoomLevel, center, isUpdating, map]);

  useEffect(() => {
    if (map) {
      // Small timeout to ensure map is fully initialized
      const timer = setTimeout(() => {
        map.setView(center, zoomLevel, { animate: false });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [map]); // Only run once when map instance is available
  
  // Listen for this map's changes
  useMapEvents({
    zoomend: (e) => {
      const newZoom = e.target.getZoom();
      const newCenter: [number, number] = [e.target.getCenter().lat, e.target.getCenter().lng];
      
      // Always update the state (for Explorer)
      setZoomLevel(newZoom);
      setCenter(newCenter);
      
      // Also call onMapChange if provided (for Comparator)
      if (!isUpdating && onMapChange) {
        onMapChange(newZoom, newCenter);
      }
    },
    moveend: (e) => {
      const newZoom = e.target.getZoom();
      const newCenter: [number, number] = [e.target.getCenter().lat, e.target.getCenter().lng];
      
      // Always update the state (for Explorer)
      setZoomLevel(newZoom);
      setCenter(newCenter);
      
      // Also call onMapChange if provided (for Comparator)
      if (!isUpdating && onMapChange) {
        onMapChange(newZoom, newCenter);
      }
    },
  });
  
  return null;
};

const MapImage: React.FC<MapImageProps> = React.forwardRef<any, MapImageProps>(({
  resolution, polygon_values, min, max, categories, polygon_type, attribute_name, 
  current_dataset, attributes_list, colormap_types, colormap_bases, current_parameters, 
  available_datasets, selectedOverlay, attribute_legend_labels, is_hml, is_legend, show_coloring, 
  is_log, default_zoom_level = 4.6, max_zoom = 10, min_zoom = 4.6, is_overlay, geojson_frame, 
  popup_name, popup_description,
  zoomLevel,
  setZoomLevel,
  center,
  setCenter,
  onMapChange,
  isUpdating
}, ref) => {

  // Map data
  const mapGeoPolygon = {
    state: { data: stateData, key: 1 },
    county: { data: countyData, key: 2 },
    huc2: { data: huc2Data, key: 3 },
    huc4: { data: huc4Data, key: 4 },
    huc6: { data: huc6Data, key: 5 },
    huc8: { data: huc8Data, key: 6 },
    overlay: { data: stateData, key: 1 },
  };

  const customColormaps: Record<string, string[]> = {
    viridis_r: ['#440154', '#482777', '#3f4a8a', '#31678e', '#26838f', '#1f9d8a', '#6cce5a', '#b6de2b', '#fee825'],
    gist_heat_r: ['#ffffff', '#ffffcc', '#ffff99', '#ffcc66', '#ff9933', '#ff6600', '#ff3300', '#cc0000', '#660000'],
    Purples_r: ['#fcfbfd', '#efedf5', '#dadaeb', '#bcbddc', '#9e9ac8', '#807dba', '#6a51a3', '#54278f', '#3f007d'],
    copper_r: ['#ffc784', '#f0bc7d', '#e1b075', '#d2a56e', '#c49967', '#b68f60', '#a78359', '#997952', '#8c6e4b'],
    tab10: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'],
    tab20: ['#1f77b4', '#aec7e8', '#ff7f0e', '#ffbb78', '#2ca02c', '#98df8a', '#d62728', '#ff9896', '#9467bd', '#c5b0d5'],
    greys: ['#ffffff', '#f0f0f0', '#d9d9d9', '#bdbdbd', '#969696', '#737373', '#525252', '#252525', '#000000'],
    rdylbu: ['#a50026', '#d73027', '#f46d43', '#fdae61', '#fee090', '#e0f3f8', '#abd9e9', '#74add1', '#4575b4', '#313695'],
    BuPu: ['#f7fcfd', '#e0ecf4', '#bfd3e6','#9ebcda', '#8c96c6', '#8c6bb1', '#88419d', '#810f7c', '#4d004b'],
    Blues: ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#08519c', '#08306b'],
    bone_r: ['#000000', '#1d1d2f', '#3a3a4e', '#57576d', '#74748c', '#9191ab', '#aeaeca', '#cbcbe9', '#e8e8ff'],
    cividis: ['#00204d', '#002d6c', '#213d6b', '#3e4c6d', '#555b6c', '#6b6b6b', '#7f7a67', '#958a5e', '#ac9a4f', '#c5aa39', '#deb91c', '#f8c901'],
    cividis_r: ['#f8c901', '#deb91c', '#c5aa39', '#ac9a4f', '#958a5e', '#7f7a67', '#6b6b6b', '#555b6c', '#3e4c6d', '#213d6b', '#002d6c', '#00204d'],
    inferno_r: ['#fcffa4', '#fbb61a', '#f4731d', '#e34f27', '#ca2e3d', '#a31754', '#78156c', '#4b0c6b', '#1b0c41', '#000004'],
    magma_r: ['#fcfdbf', '#feba86', '#fb7a5a', '#ee4f60', '#ce3261', '#a31b5b', '#721152', '#450a45', '#1b0c32', '#000004'],
    OrRd: ['#fff7ec', '#fee8c8', '#fdd49e', '#fdbb84', '#fc8d59', '#ef6548', '#d7301f', '#b30000', '#7f0000'],
    PuOr: ['#7f3b08', '#b35806', '#e08214', '#fdb863', '#fee0b6', '#f7f7f7', '#d8daeb', '#b2abd2', '#8073ac', '#542788', '#2d004b'],
    RdYlBu_r: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026'],
    RdYlGn: ['#a50026', '#d73027', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#d9ef8b', '#a6d96a', '#66bd63', '#1a9850', '#006837'],
    Reds: ['#fff5f0', '#fee0d2', '#fcbba1', '#fc9272', '#fb6a4a', '#ef3b2c', '#cb181d', '#a50f15', '#67000d'],
    Spectral: ['#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'],
    YlGn: ['#ffffe5', '#f7fcb9', '#d9f0a3', '#addd8e', '#78c679', '#41ab5d', '#238443', '#006837', '#004529'],
    YlGnBu: ['#ffffd9', '#edf8b1', '#c7e9b4', '#7fcdbb', '#41b6c4', '#1d91c0', '#225ea8', '#253494', '#081d58'],
    YlOrBr: ['#ffffe5', '#fff7bc', '#fee391', '#fec44f', '#fe9929', '#ec7014', '#cc4c02', '#993404', '#662506'],
    YlOrRd: ['#ffffcc', '#ffeda0', '#fed976', '#feb24c', '#fd8d3c', '#fc4e2a', '#e31a1c', '#bd0026', '#800026'],
  };

  const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection>(mapGeoPolygon["state"].data);
  const [highlightedFeature, setHighlightedFeature] = useState<any>(null);
  const [selectedFeature, setSelectedFeature] = useSessionStorage<any>("selectedFeature",null);

  function createCategoryColorMap(
    polygonValues: { [key: string]: string },
    colormap: string[]
  ): { [category: string]: string } {
    const uniqueCategories = Array.from(new Set(Object.values(polygonValues)));
    const categoryColorMap: { [category: string]: string } = {};

    uniqueCategories.forEach((cat, i) => {
      categoryColorMap[cat] = colormap[i % colormap.length];
    });
    return categoryColorMap;
  }

  let categoryColorMap = undefined;
  if (colormap_types in customColormaps){
    const selectedColorMap = customColormaps[colormap_types];
    categoryColorMap = createCategoryColorMap(polygon_values, selectedColorMap);
  }

  var bin_number = 0;
  if (polygon_type == "Discrete") {
    bin_number = Object.keys(categories).length;
  }

  useEffect(() => {
    setGeoJsonData(mapGeoPolygon[resolution].data);
  }, [resolution]);

  function getColorByValue(value: number): string {
    let colorPalette: string[];
    const colormapType = colormap_types;

    if(is_hml){
      colorPalette = customColormaps["HML"] || ['#006400', '#FFFFE0', '#8B0000'];
      return chroma.scale(colorPalette).domain([0, 1])(value).hex();
    }
    
    if (customColormaps[colormapType]) {
      colorPalette = customColormaps[colormapType];
    } else {
      colorPalette = chroma.scale(colormapType as chroma.BrewerPaletteName).colors(20).reverse();
    }
    const numCategories = Object.keys(categories).length;

    if (is_overlay === "True") {
      if (polygon_type === "Discrete" && numCategories > 0) {
        const shortenedPalette = colorPalette.slice(0, numCategories);
        return chroma.scale(shortenedPalette).domain([0, 1])(value).hex();
      }
      if (polygon_type === "Continuous") {
        return chroma.scale(colorPalette).domain([0, 1])(value).hex();
      }
    }
    return chroma.scale(colorPalette).domain([0, 1])(value).hex();
  }

  function getColor(id: string): string {
    if(!show_coloring){
      return "#dbd3bd";
    }

    const selectedData = polygon_values;
    let matchedValue = selectedData[id];

    if(is_hml){
      switch(matchedValue){
        case "High": return "#006400";
        case "Medium": return "#FFFFE0";
        case "Low": return "#8B0000";
      }
    }

    if (polygon_type == "Discrete") {
      const normalizedValue = categories[matchedValue] / (Object.keys(categories).length - 1);
      return getColorByValue(normalizedValue);
    }

    if (matchedValue == undefined) {
      return "#dbd3bd";
    }
    const colValue = Number(matchedValue);

    if ((max! - min!) === 0) return getColorByValue(0)
    const normalizedRank = (colValue - min!) / (max! - min!);
    return getColorByValue(normalizedRank);
  }

  const styleFeature = (feature: any) => {
    const isHighlighted = highlightedFeature === feature;
    
    return {
      fillColor: getColor(feature.id),
      weight: 2.34 - ((max_zoom - zoomLevel) / (max_zoom - min_zoom) * 1.87),
      opacity: 1,
      color: "black",
      fillOpacity: 0.7,
      zIndex: 0,
    };
  };

  const resetHighlight = () => {
    if (highlightedFeature) {
      setHighlightedFeature(null);
    }
  };

  const onEachFeature = useCallback(
    (feature: any, layer: any) => {
      layer.on({
        add: () => layer.bringToBack(),
        click: () => {
          if (highlightedFeature) {
            resetHighlight();
          }
          setHighlightedFeature(feature);
          setSelectedFeature(feature);

          if (is_legend !== "ON") {
            return;
          }

          const selectedData = polygon_values;
          let matchedValue = selectedData[String(feature.id)];

          if (!matchedValue) {
            return "#FFFF00";
          }
          const colValue = Number(matchedValue);
          var popupValue = String(colValue.toFixed(2));
          if (isNaN(colValue)) {
            popupValue = matchedValue;
          } else {
            if (colValue > 1000000) { popupValue = colValue.toExponential(3);}
          }

          let plus_tag = "";

          const quantityType = { ...current_parameters };
          const qTypeArrayKeys = (current_parameters == "Builder")?"Builder":Object.keys(quantityType);
          
          const main_name = available_datasets.titles[available_datasets.names.indexOf(current_dataset)];
          const side_name = attributes_list.titles[attributes_list.names.indexOf(attribute_name)];

          let layer_text = `<div style="font-size: 14px; line-height: 1.5;">\n`;
          if(feature.properties.name || feature.properties.huc_name) layer_text += `<b style="font-size: 16px; color: #333;">${feature.properties.name || feature.properties.huc_name}</b><br>\n`;
          if(main_name) layer_text += `<b style="font-size: 14px; color: #666;">${main_name}</b><br>\n`;
          if(side_name && plus_tag) layer_text += `${side_name} layer_text += ${plus_tag}<br>\n`;
          if(popupValue)  layer_text += `<span style="font-weight: bold; color: #007bff;">${popupValue}</span>\n`;
          if (popup_description && popup_description[feature.id]) 
            for (const pdii in popup_description[feature.id])
              layer_text+= `<br><span style="color: #555;">${popup_description[feature.id][pdii]}</span>\n`;             
          layer_text += `</div>`;

          layer.bindPopup(layer_text).openPopup();
        },
      });
    },
    [polygon_values, attribute_name, highlightedFeature, setHighlightedFeature, current_parameters, selectedFeature]
  );

  const pointToLayerForOverlay = useCallback((feature: any, latlng: L.LatLng) => {
    const index = geojson_frame!.features.indexOf(feature);
    const id = (index + 1).toString();
    const category = polygon_values[id];

    const color = categoryColorMap ? categoryColorMap[category] || "#20ea2eff" :  "#20ea2eff";
    const marker = L.circleMarker(latlng, {
      radius: 4,
      color: color,
      fillColor: color,
      weight: 1,
      opacity: 1,
      fillOpacity: 0.8
    });
    if (popup_name && id && popup_name[id]) {
      marker.bindPopup(popup_name[id]);
    }

    return marker;
  }, [attribute_name, categoryColorMap]);

  const onEachOverlayFeature = useCallback((feature: any, layer: L.Layer) => {
    if (feature.geometry.type === "Point") {
      return;
    }

    const index = geojson_frame!.features.indexOf(feature);
    const id = (index + 1).toString();
    const category = polygon_values[id];
    const color = categoryColorMap ? categoryColorMap[category] || "#20ea2eff" :  "#20ea2eff"; 

    (layer as L.Path).setStyle({
      color,
      weight: 3,
      opacity: 1
    });
    if (category) 
      layer.bindPopup(`${category}`);        
  }, [attribute_name, polygon_values]);

  return (
    <MapContainer
      ref={ref}
      className="map-container"
      center={center}
      zoom={zoomLevel}
      zoomSnap={0.15}
      zoomDelta={0.20}
      minZoom={min_zoom}
      maxZoom={max_zoom}
      maxBounds={[
        [15, -140],
        [60, -50],
      ]}
      style={{ height: "100%", width: "100%" }}
      key={mapGeoPolygon[resolution].key}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />

      <ResetMapButton defaultCenter={[38.5, -99.3]} defaultZoom={default_zoom_level} />

      {is_overlay === "False" && geoJsonData &&(
      <GeoJSON
        data={geoJsonData}
        style={styleFeature}
        key={`main-${attribute_name}`}
        onEachFeature={onEachFeature}
      />
      )}

      {is_overlay === "True" && geojson_frame && (
        <GeoJSON
          data={geojson_frame}
          key={`overlay-${attribute_name}`}
          onEachFeature={onEachOverlayFeature}
          pointToLayer={pointToLayerForOverlay}
        />
      )}

      {is_legend === "ON" && (
      <Legend getColor={getColorByValue}
        key={`overlay-${attribute_name}`}
        current_dataset={current_dataset}
        minValue={min || 0} maxValue={max || 1}
        categories={categories}
        polygon_type={polygon_type} bins={bin_number}
        polygon_values={polygon_values}
        attribute_legend_labels={attribute_legend_labels}
        is_log={is_log}
      />)}

      {(selectedOverlay != "None") && <OverlayLegend selectedOverlay={selectedOverlay} />}
      
      <MapSyncHandler 
        zoomLevel={zoomLevel}
        center={center}
        setZoomLevel={setZoomLevel}
        setCenter={setCenter}
        onMapChange={onMapChange}
        isUpdating={isUpdating}
      />

    </MapContainer>
  );
});

(MapImage as any).displayName = 'MapImage';

export default MapImage;